import type { SettingsPatchReviewModel } from "./types";
import type { formQueryKeys } from "@/api/forms/form-query";
import type { bookableGroupQueryKeys } from "@/api/provider/admin/bookable-group-query";
import type { bookableQueryKeys } from "@/api/provider/admin/bookable-query";
import type { bookingEventQueryKeys } from "@/api/provider/admin/booking-event-query";
import type { providerQueryKeys } from "@/api/provider/admin/provider-query";
import type { roomQueryKeys } from "@/api/provider/admin/room-query";
import type { serviceQueryKeys } from "@/api/provider/admin/service-query";
import type { chargeQueryKeys } from "@/api/provider/billing/charge-query";
import type { merchantAccountQueryKeys as billingMerchantAccountQueryKeys } from "@/api/provider/billing/merchant-account-query";
import type { ruleSetQueryKeys } from "@/api/provider/billing/rule-set-query";
import type { superbillFavoriteCptCodesQueryKey } from "@/api/provider/billing/superbill";
import type { calendarViewPresetQueryKeys } from "@/api/provider/calendar-view-presets/calendar-view-presets-query";
import type { callQueryKeys } from "@/api/provider/comms/call";
import type { emailQueryKeys } from "@/api/provider/comms/email/email-query";
import type { generatedDocumentQueryKeys } from "@/api/provider/generated-document/generated-document-query";
import type { itemQueryKeys } from "@/api/provider/inventory/item-query";
import type { membershipQueryKeys } from "@/api/provider/inventory/membership-query";
import type {
  packageQueryKeys,
  patientSoldPackageQueryKeys,
} from "@/api/provider/inventory/package-query";
import type {
  labQueryKeys,
  labTestsKeyPrefix,
} from "@/api/provider/lab/lab-query";
import type { pipelineQueryKeys } from "@/api/provider/opportunities/pipelines-query";
import type { platformQueryKeys as campaignPlatformQueryKeys } from "@/api/provider/opportunities/pipelines-query";
import type { platformQueryKeys as platformIntegrationQueryKeys } from "@/api/provider/opportunities/platforms-query";
import type { reviewQueryKeys } from "@/api/provider/reviews/reviews-query";
import type { noteQueryKeys } from "@/api/provider/scribe/note-query";
import type { carePlanTagQueryKeys } from "@/api/provider/settings/care-plan-tag-query";
import type { carePlanTemplateQueryKeys } from "@/api/provider/settings/care-plan-template-query";
import type { documentTemplateQueryKeys } from "@/api/provider/settings/document-template-query";
import type { taskSettingsQueryKeys } from "@/api/provider/tasks/task-settings-query";
import type { taskQueryKeys } from "@/api/provider/tasks/tasks-query";
import type { teamsQueryKeys } from "@/api/provider/teams/teams-query";
import type { documentTypeQueryKeys } from "@/api/provider/user/patient/document-type-query";
import type { patientQueryKeys } from "@/api/provider/user/patient/patient-query";
import type { cohortQueryKeys } from "@/api/query/provider/comms/blast";
import type { outreachSequenceQueryKeys } from "@/api/query/provider/comms/outreach-sequence";
import type { webChatQueryKeys } from "@/api/query/web-chat";
import type { mailgunDomainQueryKeys } from "@/api/settings/mailgun-domain-query";
import type { rolesQueryKeys } from "@/api/settings/roles-query";
import type { spakinectQueryKeys } from "@/api/spakinect/gfe";
import type { chargesQueryKeys } from "@/query/billing/charges";
import type { issuedGiftCardQueryKeys } from "@/query/billing/issued-gift-card";
import type { tipDisplayQueryKeys } from "@/query/billing/tip-displays";
import type { calendarQueryKeys } from "@/query/check-in/calendar/calendar-query";
import type { eventQueryKeys } from "@/query/check-in/calendar/event-query";
import type { socialQueryKeys } from "@/query/comms/social";
import type { emailTemplateQueryKeys } from "@/query/email-template";
import type { accessLabsQueryKeys } from "@/query/integrations/access-labs";
import type { coupaQueryKeys } from "@/query/integrations/coupa";
import type { googleCalendarQueryKeys } from "@/query/integrations/google-calendar";
import type { googleCalendarOutboundQueryKeys } from "@/query/integrations/google-calendar-outbound";
import type { inbodyQueryKeys } from "@/query/integrations/inbody";
import type { qualiphyQueryKeys } from "@/query/integrations/qualiphy";
import type { quickBooksQueryKeys } from "@/query/integrations/quickbooks/keys";
import type { categoryQueryKeys } from "@/query/inventory/category";
import type { commissionQueryKeys } from "@/query/inventory/commission";
import type { memberQueryKeys } from "@/query/inventory/member";
import type { purchaseOrderQueryKeys } from "@/query/inventory/purchase-order";
import type { shipmentQueryKeys } from "@/query/inventory/shipment";
import type { stockQueryKeys } from "@/query/inventory/stock";
import type { supplierQueryKeys } from "@/query/inventory/supplier";
import type { assistantQueryKeys } from "@/query/keys/assistants";
import type { loyaltyConfigQueryKeys } from "@/query/keys/loyalty-config";
import type { suggestionQueryKeys } from "@/query/keys/suggestions";
import type { medicationsQueryKeys } from "@/query/medications/medications";
import type { notificationQueryKeys } from "@/query/notifications/notifications";
import type { deskPhonesQueryKeys } from "@/query/settings/desk-phones";
import type { dialExtensionsQueryKeys } from "@/query/settings/dial-extensions";
import type { insuranceCarrierQueryKeys } from "@/query/settings/insurance-carriers";
import type { locationQueryKeys } from "@/query/settings/location";
import type { measurementFieldQueryKeys } from "@/query/settings/measurement-fields";
import type { measurementGroupQueryKeys } from "@/query/settings/measurement-groups";
import type { merchantAccountQueryKeys as settingsMerchantAccountQueryKeys } from "@/query/settings/merchant-account";
import type { patientCreditReasonQueryKeys } from "@/query/settings/patient-credit-reason";
import type { patientPermissionKeys } from "@/query/settings/patient-permissions";
import type { paymentMediumQueryKeys } from "@/query/settings/payment-medium";
import type { phoneNumberConfigQueryKeys } from "@/query/settings/phone-number-config";
import type { posDeviceQueryKeys } from "@/query/settings/pos-devices";
import type { taxQueryKeys } from "@/query/settings/tax";
import type { webhookQueryKeys } from "@/query/settings/webhooks";
import type { tenantQueryKeys } from "@/query/tenant";

/**
 * React Query keys owned by each settings-engine target.
 *
 * Ask Decoda applies patches server-side, so no console mutation runs and nothing marks the
 * cached copy of the edited settings stale. After an apply we invalidate the keys of every target
 * the applied units wrote to.
 *
 * Every key is `satisfies`-checked against the factory that owns it. The imports are type-only, so
 * they are erased at build time and none of those modules reach the AI sidebar's bundle, but a key
 * that drifts from its factory fails `tsc` on the line that is wrong.
 *
 * An empty array means the target's screen does not read through React Query (plain `useState` +
 * fetch, or the tenant/user context), so there is no cache to invalidate.
 */
const CURRENT_USER_TARGETS = new Set([
  "provider.calendarPreferences",
  "provider.profile",
  "tenant.roles",
  "tenant.users",
]);
const TENANT_CONTEXT_TARGETS = new Set([
  "tenant.brand",
  "tenant.calendarColorMode",
  "tenant.workspaceNavigation",
  "tenant.schedulingDefaults",
  "tenant.communications",
  "tenant.invoicing",
  "tenant.paymentSettings",
  "tenant.catalogDefaults",
  "tenant.inventoryCounting",
  "tenant.analyticsDefaults",
  "tenant.auth",
  "tenant.clinicGoogleCalendar",
  "tenant.patientSettings",
  "tenant.documentReview",
  "tenant.clinicalNotes",
  "tenant.selfScheduling",
  "tenant.inboundTexting",
  "tenant.socialLeads",
  "tenant.staffCallRinging",
  "tenant.googleTagManager",
  "tenant.paymentManagement",
  "tenant.patientPortal",
]);

const TARGET_QUERY_KEYS: Record<string, readonly (readonly unknown[])[]> = {
  "email.domains": [
    ["settings", "mailgun-domain"] satisfies typeof mailgunDomainQueryKeys.all,
  ],
  "email.header": [["email", "header"] satisfies typeof emailQueryKeys.header],
  "email.signature": [
    ["email", "signature"] satisfies typeof emailQueryKeys.signature,
  ],
  "email.templates": [
    ["email", "templates"] satisfies typeof emailTemplateQueryKeys.all,
  ],
  // Personal preferences live on the user context, not a query.
  "provider.calendarPreferences": [],
  "provider.googleCalendarSubscriptions": [
    ["google-calendar"] satisfies typeof googleCalendarQueryKeys.all,
    [
      "googleCalendarOutbound",
    ] satisfies typeof googleCalendarOutboundQueryKeys.all,
  ],
  "provider.notificationPreferences": [
    ["notifications"] satisfies typeof notificationQueryKeys.all,
  ],
  "provider.profile": [["providers"] satisfies typeof providerQueryKeys.all],
  "tenant.accessLabAccounts": [
    ["access-labs"] satisfies typeof accessLabsQueryKeys.all,
  ],
  "tenant.accessLabPanels": [
    ["lab", "accessPanels"] satisfies ReturnType<
      typeof labQueryKeys.accessPanels
    >,
  ],
  // Read-only: the Access compendium lookup never applies a patch.
  "tenant.accessLabTests": [],
  "tenant.alert_task_rules": [
    ["task-settings"] satisfies typeof taskSettingsQueryKeys.all,
    ["tasks"] satisfies typeof taskQueryKeys.all,
  ],
  "tenant.apiKey": [
    ["tenant", "api-keys"] satisfies ReturnType<typeof tenantQueryKeys.apiKeys>,
  ],
  "tenant.appointmentStyles": [
    ["calendar"] satisfies typeof calendarQueryKeys.all,
  ],
  "tenant.assistants": [
    ["assistants"] satisfies typeof assistantQueryKeys.all,
    ["assistant-tools"] satisfies ReturnType<typeof assistantQueryKeys.tools>,
    ["settings", "behavior-presets"] satisfies ReturnType<
      typeof assistantQueryKeys.behaviorPresets
    >,
    ["web-chat"] satisfies typeof webChatQueryKeys.all,
  ],
  "tenant.bookables": [
    ["provider", "admin", "bookables"] satisfies typeof bookableQueryKeys.all,
    [
      "provider",
      "admin",
      "bookable-groups",
    ] satisfies typeof bookableGroupQueryKeys.all,
  ],
  "tenant.bookingEvents": [
    [
      "provider",
      "admin",
      "booking-event",
    ] satisfies typeof bookingEventQueryKeys.all,
  ],
  "tenant.brand": [["tenant"] satisfies typeof tenantQueryKeys.all],
  "tenant.calendarColorMode": [
    ["calendar"] satisfies typeof calendarQueryKeys.all,
  ],
  "tenant.calendarViewPresets": [
    ["calendar-view-presets"] satisfies typeof calendarViewPresetQueryKeys.all,
  ],
  "tenant.care_plan_tags": [
    ["settings", "care-plan-tags"] satisfies typeof carePlanTagQueryKeys.all,
  ],
  "tenant.care_plan_templates": [
    [
      "settings",
      "care-plan-templates",
    ] satisfies typeof carePlanTemplateQueryKeys.all,
  ],
  "tenant.categories": [
    ["inventory", "category"] satisfies typeof categoryQueryKeys.all,
  ],
  "tenant.charges": [
    ["billing", "charge"] satisfies typeof chargeQueryKeys.all,
    ["billing", "charges"] satisfies typeof chargesQueryKeys.all,
  ],
  "tenant.commissions": [
    ["inventory", "commissions"] satisfies typeof commissionQueryKeys.all,
  ],
  "tenant.cohorts": [["cohorts"] satisfies typeof cohortQueryKeys.all],
  "tenant.coupaCommodityRules": [["coupa"] satisfies typeof coupaQueryKeys.all],
  "tenant.coupaConnection": [["coupa"] satisfies typeof coupaQueryKeys.all],
  "tenant.coupaIntegration": [["coupa"] satisfies typeof coupaQueryKeys.all],
  "tenant.coupaItemLinks": [["coupa"] satisfies typeof coupaQueryKeys.all],
  "tenant.coupaItemRules": [["coupa"] satisfies typeof coupaQueryKeys.all],
  "tenant.coupaReasonCodes": [["coupa"] satisfies typeof coupaQueryKeys.all],
  "tenant.coupaSupplierLinks": [["coupa"] satisfies typeof coupaQueryKeys.all],
  "tenant.deskPhones": [
    ["settings", "desk-phones"] satisfies typeof deskPhonesQueryKeys.all,
  ],
  "tenant.dialExtensions": [
    [
      "settings",
      "dial-extensions",
    ] satisfies typeof dialExtensionsQueryKeys.all,
  ],
  // Both lists: the picker in the document upload/edit dialogs reads the patient-facing
  // list, while the admin list backs the management view.
  "tenant.documentTypes": [
    ["document-types"] satisfies typeof documentTypeQueryKeys.all,
    ["admin", "document-types"] satisfies typeof documentTypeQueryKeys.admin,
  ],
  "tenant.eventIcons": [["calendar"] satisfies typeof calendarQueryKeys.all],
  "tenant.forms": [["forms"] satisfies typeof formQueryKeys.all],
  // Replacing forms changes which form each service asks for and which completions count, so the
  // services list and appointment checklists refresh along with the forms' own queries.
  "tenant.formReplacements": [
    ["forms"] satisfies typeof formQueryKeys.all,
    ["provider", "admin", "services"] satisfies typeof serviceQueryKeys.all,
    ["calendar"] satisfies typeof calendarQueryKeys.all,
  ],
  "tenant.workspaceNavigation": [
    ["tenant"] satisfies typeof tenantQueryKeys.all,
  ],
  "tenant.schedulingDefaults": [
    ["tenant"] satisfies typeof tenantQueryKeys.all,
  ],
  "tenant.communications": [["tenant"] satisfies typeof tenantQueryKeys.all],
  "tenant.invoicing": [["tenant"] satisfies typeof tenantQueryKeys.all],
  "tenant.paymentSettings": [["tenant"] satisfies typeof tenantQueryKeys.all],
  "tenant.catalogDefaults": [["tenant"] satisfies typeof tenantQueryKeys.all],
  "tenant.inventoryCounting": [["tenant"] satisfies typeof tenantQueryKeys.all],
  "tenant.analyticsDefaults": [["tenant"] satisfies typeof tenantQueryKeys.all],
  "tenant.auth": [["tenant"] satisfies typeof tenantQueryKeys.all],
  "tenant.clinicGoogleCalendar": [
    ["tenant"] satisfies typeof tenantQueryKeys.all,
  ],
  "tenant.patientSettings": [["tenant"] satisfies typeof tenantQueryKeys.all],
  "tenant.documentReview": [["tenant"] satisfies typeof tenantQueryKeys.all],
  "tenant.clinicalNotes": [["tenant"] satisfies typeof tenantQueryKeys.all],
  "tenant.selfScheduling": [["tenant"] satisfies typeof tenantQueryKeys.all],
  "tenant.inboundTexting": [["tenant"] satisfies typeof tenantQueryKeys.all],
  "tenant.socialLeads": [["tenant"] satisfies typeof tenantQueryKeys.all],
  "tenant.staffCallRinging": [["tenant"] satisfies typeof tenantQueryKeys.all],
  "tenant.googleTagManager": [["tenant"] satisfies typeof tenantQueryKeys.all],
  "tenant.paymentManagement": [["tenant"] satisfies typeof tenantQueryKeys.all],
  // Denominations are gift-card items in the catalog.
  "tenant.giftCardDenominations": [
    ["inventory", "items"] satisfies typeof itemQueryKeys.all,
  ],
  "tenant.inbodyIntegration": [["inbody"] satisfies typeof inbodyQueryKeys.all],
  "tenant.insuranceCarriers": [
    [
      "settings",
      "insurance-carriers",
    ] satisfies typeof insuranceCarrierQueryKeys.all,
  ],
  "tenant.issuedGiftCards": [
    [
      "billing",
      "issued-gift-cards",
    ] satisfies typeof issuedGiftCardQueryKeys.all,
  ],
  // Junction panels are read through the `tests` catalog query, cached per filter set.
  "tenant.junctionLabPanels": [
    ["lab", "tests"] satisfies typeof labTestsKeyPrefix,
  ],
  // Read-only: the Junction labs and biomarkers lookup never applies a patch.
  "tenant.junctionLabMarkers": [],
  "tenant.locations": [
    ["settings", "location"] satisfies typeof locationQueryKeys.all,
  ],
  "tenant.loyaltyPoints": [
    ["loyalty-config"] satisfies typeof loyaltyConfigQueryKeys.all,
  ],
  "tenant.measurementFields": [
    [
      "settings",
      "measurement-fields",
    ] satisfies typeof measurementFieldQueryKeys.all,
  ],
  "tenant.measurementGroups": [
    [
      "settings",
      "measurement-groups",
    ] satisfies typeof measurementGroupQueryKeys.all,
  ],
  "tenant.medications": [
    ["medications"] satisfies typeof medicationsQueryKeys.all,
  ],
  "tenant.members": [
    ["inventory", "members"] satisfies typeof memberQueryKeys.all,
    ["inventory", "memberships"] satisfies typeof membershipQueryKeys.all,
  ],
  "tenant.memberships": [
    ["inventory", "memberships"] satisfies typeof membershipQueryKeys.all,
  ],
  "tenant.merchantAccounts": [
    [
      "settings",
      "merchant-accounts",
    ] satisfies typeof settingsMerchantAccountQueryKeys.all,
    [
      "billing",
      "merchant-account",
    ] satisfies typeof billingMerchantAccountQueryKeys.all,
  ],
  // Read-only: the target never applies a patch, so nothing goes stale.
  "tenant.modules": [],
  "tenant.noteAttachmentTags": [
    ["scribe", "notes"] satisfies typeof noteQueryKeys.all,
  ],
  "tenant.noteTemplates": [
    ["scribe", "notes"] satisfies typeof noteQueryKeys.all,
  ],
  "tenant.packages": [
    ["inventory", "packages"] satisfies typeof packageQueryKeys.all,
  ],
  "tenant.patientPortal": [
    [
      "settings",
      "patient-permissions",
    ] satisfies typeof patientPermissionKeys.all,
    ["tenant"] satisfies typeof tenantQueryKeys.all,
  ],
  "tenant.patientCreditReasons": [
    [
      "settings",
      "patientCreditReasons",
    ] satisfies typeof patientCreditReasonQueryKeys.all,
  ],
  "tenant.patientTags": [
    ["user", "patient", "patient-tags"] satisfies ReturnType<
      typeof patientQueryKeys.patientTags
    >,
  ],
  "tenant.paymentMediums": [
    ["settings", "paymentMedium"] satisfies typeof paymentMediumQueryKeys.all,
  ],
  "tenant.phoneNumber": [
    [
      "settings",
      "phone-number-config",
    ] satisfies typeof phoneNumberConfigQueryKeys.all,
  ],
  "tenant.phoneNumbers": [
    [
      "settings",
      "phone-number-config",
    ] satisfies typeof phoneNumberConfigQueryKeys.all,
  ],
  "tenant.pipelines": [["pipelines"] satisfies typeof pipelineQueryKeys.all],
  "tenant.platforms": [
    ["platform-integrations"] satisfies typeof platformIntegrationQueryKeys.all,
    ["campaign-platforms"] satisfies typeof campaignPlatformQueryKeys.all,
  ],
  "tenant.posDevices": [
    ["settings", "pos-devices"] satisfies typeof posDeviceQueryKeys.all,
  ],
  // Read-only permission catalog; no console query cache to invalidate after a patch.
  "tenant.permissions": [],
  "tenant.products": [
    ["inventory", "items"] satisfies typeof itemQueryKeys.all,
  ],
  // The promotions screen fetches into local state.
  "tenant.promotions": [["promotions-list"]],
  "tenant.qualiphyIntegration": [
    ["qualiphy"] satisfies typeof qualiphyQueryKeys.all,
  ],
  "tenant.quickbooksAccountMapping": [
    ["quickbooks"] satisfies typeof quickBooksQueryKeys.all,
  ],
  // Live QuickBooks data with no local cache to invalidate.
  "tenant.quickbooksChartOfAccounts": [],
  "tenant.quickbooksCompanies": [
    ["quickbooks"] satisfies typeof quickBooksQueryKeys.all,
  ],
  "tenant.quickbooksCustomers": [
    ["quickbooks"] satisfies typeof quickBooksQueryKeys.all,
  ],
  // The referral-programs settings page fetches into local state, not a query.
  "tenant.referralProgram": [],
  "tenant.sequences": [
    ["outreach-sequences"] satisfies typeof outreachSequenceQueryKeys.all,
  ],
  "tenant.reviewGating": [
    ["reviews", "setup"] satisfies ReturnType<typeof reviewQueryKeys.setup>,
  ],
  "tenant.roles": [
    ["settings", "roles"] satisfies typeof rolesQueryKeys.all,
    ["providers"] satisfies typeof providerQueryKeys.all,
  ],
  "tenant.rooms": [
    ["provider", "admin", "room"] satisfies typeof roomQueryKeys.all,
  ],
  "tenant.rule_sets": [
    ["billing", "rule-set"] satisfies typeof ruleSetQueryKeys.all,
  ],
  "tenant.schedulingBlacklist": [
    ["user", "patient", "blacklist"] satisfies ReturnType<
      typeof patientQueryKeys.blacklist
    >,
  ],
  "tenant.services": [
    ["provider", "admin", "services"] satisfies typeof serviceQueryKeys.all,
  ],
  "tenant.shiftManagement": [
    ["calendar", "event", "shifts"] satisfies ReturnType<
      typeof eventQueryKeys.shifts
    >,
  ],
  // Shipments carry the quantities, so a write moves the stock's on-hand total and changes
  // which lots a dose can be recorded against.
  "tenant.shipments": [
    ["inventory", "stock", "shipments"] satisfies typeof shipmentQueryKeys.all,
    ["inventory", "stock"] satisfies typeof stockQueryKeys.all,
    ["medications"] satisfies typeof medicationsQueryKeys.all,
  ],
  "tenant.socialConnections": [
    ["comms", "social"] satisfies typeof socialQueryKeys.all,
  ],
  "tenant.soldPackages": [
    ["patient-packages"] satisfies typeof patientSoldPackageQueryKeys.root,
    ["inventory", "packages"] satisfies typeof packageQueryKeys.all,
  ],
  "tenant.spakinectFavorites": [
    ["spakinect"] satisfies typeof spakinectQueryKeys.all,
  ],
  "tenant.stock": [["inventory", "stock"] satisfies typeof stockQueryKeys.all],
  "tenant.suggestions": [
    ["suggestions"] satisfies typeof suggestionQueryKeys.all,
    ["personal-suggestions"] satisfies typeof suggestionQueryKeys.personal,
  ],
  "tenant.superbillFavoriteCptCodes": [
    [
      "superbill-favorite-cpt-codes",
    ] satisfies typeof superbillFavoriteCptCodesQueryKey,
  ],
  "tenant.suppliers": [
    ["inventory", "supplier"] satisfies typeof supplierQueryKeys.all,
  ],
  // Read-only: the orderable part numbers behind purchase order line items.
  "tenant.supplierCatalog": [],
  "tenant.purchaseOrders": [
    ["inventory", "purchaseOrder"] satisfies typeof purchaseOrderQueryKeys.all,
  ],
  // Routing tags are read as part of each call row.
  "tenant.tags": [["comms", "call"] satisfies typeof callQueryKeys.all],
  "tenant.task_settings": [
    ["task-settings"] satisfies typeof taskSettingsQueryKeys.all,
    ["tasks"] satisfies typeof taskQueryKeys.all,
  ],
  "tenant.taxes": [["settings", "taxes"] satisfies typeof taxQueryKeys.all],
  "tenant.teams": [["teams"] satisfies typeof teamsQueryKeys.all],
  // The tip-display section fetches into local state; checkout reads the query.
  "tenant.tipDisplays": [
    ["billing", "tip-displays"] satisfies typeof tipDisplayQueryKeys.all,
  ],
  "tenant.treatmentPlanTemplates": [
    [
      "settings",
      "document-templates",
    ] satisfies typeof documentTemplateQueryKeys.all,
    [
      "patient-documents",
      "templates",
    ] satisfies typeof generatedDocumentQueryKeys.templates,
  ],
  "tenant.users": [["providers"] satisfies typeof providerQueryKeys.all],
  "tenant.webhooks": [
    ["settings", "webhooks"] satisfies typeof webhookQueryKeys.all,
  ],
};

export function isKnownSettingsTarget(targetId: string): boolean {
  return targetId in TARGET_QUERY_KEYS;
}

export function settingsTargetsRefreshCurrentUser(
  targetIds: readonly string[],
): boolean {
  return targetIds.some((targetId) => CURRENT_USER_TARGETS.has(targetId));
}

export function settingsTargetsRefreshTenant(
  targetIds: readonly string[],
): boolean {
  return targetIds.some((targetId) => TENANT_CONTEXT_TARGETS.has(targetId));
}

/**
 * Targets the applied units wrote to. An atomic closure applies as a whole, so a selected change
 * contributes every unit's target, context rows included.
 */
export function appliedSettingsTargetIds(
  model: SettingsPatchReviewModel,
  appliedUnitIds: readonly string[],
): string[] {
  const applied = new Set(appliedUnitIds);
  const targetIds = new Set<string>();
  for (const change of model.changes) {
    if (!change.unitIds.some((unitId) => applied.has(unitId))) continue;
    for (const unit of change.units) {
      if (unit.targetId) targetIds.add(unit.targetId);
    }
  }
  if (targetIds.size === 0 && model.targetId) targetIds.add(model.targetId);
  return Array.from(targetIds);
}

/** Query keys to invalidate for the given targets, deduplicated. */
export function settingsTargetQueryKeys(
  targetIds: readonly string[],
): (readonly unknown[])[] {
  const seen = new Set<string>();
  const keys: (readonly unknown[])[] = [];
  for (const targetId of targetIds) {
    for (const key of TARGET_QUERY_KEYS[targetId] ?? []) {
      const fingerprint = JSON.stringify(key);
      if (seen.has(fingerprint)) continue;
      seen.add(fingerprint);
      keys.push(key);
    }
  }
  return keys;
}
